// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  normalizeApiTokenList,
  normalizeCreatedApiToken,
  type ApiToken,
  type CreatedApiToken,
} from '@/features/profile/apiTokens';
import { useI18n } from '@/i18n';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import { sendAndAwaitAck } from '@/services/wsAck';

const REQUEST_TIMEOUT_MS = 15000;

type UseApiTokensParams = {
  apiBaseUrl: string;
  token?: string | null;
};

class ApiTokenRequestError extends Error {
  constructor(public readonly code: string) {
    super(code);
  }
}

/**
 * The user's personal access tokens, managed over the BFF socket: `apiToken.list.request`,
 * `apiToken.create` and `apiToken.revoke`, each answered by a frame carrying our requestId.
 *
 * The one-time secret in `apiToken.created` is handed to the caller and never kept here: the
 * hook's own state holds the list shape only, so nothing on this screen can leak it later.
 */
export const useApiTokens = ({ apiBaseUrl, token }: UseApiTokensParams) => {
  const { t } = useI18n();
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const tokenRef = useRef(token);
  // Read through a ref so the callbacks below stay stable: the mount effect lists once per
  // sign-in, and must not re-run because a translator or token instance changed.
  const tRef = useRef(t);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'create' | 'revoke' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const request = useCallback(async (message: Record<string, unknown>, ackType: string) => {
    const socket = socketRef.current;
    socket.setAuthToken(tokenRef.current ?? null);
    const requestId = createRequestId();
    const frame = await sendAndAwaitAck(socket, message, [ackType, 'error'], {
      requestId,
      // A generic `error` frame without our requestId belongs to someone else's request; the
      // BFF stamps ours on validation and upstream failures alike.
      match: (candidate) => candidate.requestId === requestId,
      timeoutMs: REQUEST_TIMEOUT_MS,
    });
    if (frame.type === 'error') {
      throw new ApiTokenRequestError(typeof frame.code === 'string' ? frame.code : 'ws.handler.failed');
    }
    return frame;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const frame = await request({ type: 'apiToken.list.request' }, 'apiToken.list');
      setTokens(normalizeApiTokenList(frame.tokens));
    } catch (err) {
      console.warn('[profile] apiToken.list.request failed', err);
      setError(tRef.current('profile.apiTokensLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [request]);

  const create = useCallback(
    async (name: string): Promise<CreatedApiToken | null> => {
      setBusy('create');
      setError(null);
      try {
        const frame = await request({ type: 'apiToken.create', name }, 'apiToken.created');
        const created = normalizeCreatedApiToken(frame);
        if (!created) {
          throw new ApiTokenRequestError('apiToken.create.failed');
        }
        const { token: _secret, ...listed } = created;
        setTokens((previous) => [listed, ...previous.filter((entry) => entry.id !== listed.id)]);
        return created;
      } catch (err) {
        const code = err instanceof ApiTokenRequestError ? err.code : 'apiToken.create.failed';
        console.warn('[profile] apiToken.create failed', code);
        setError(
          code === 'apiToken.create.limit'
            ? tRef.current('profile.apiTokenLimitReached')
            : tRef.current('profile.apiTokenCreateFailed')
        );
        return null;
      } finally {
        setBusy(null);
      }
    },
    [request]
  );

  const revoke = useCallback(
    async (id: string): Promise<boolean> => {
      setBusy('revoke');
      setError(null);
      try {
        await request({ type: 'apiToken.revoke', tokenId: id }, 'apiToken.revoked');
        setTokens((previous) => previous.filter((entry) => entry.id !== id));
        return true;
      } catch (err) {
        const code = err instanceof ApiTokenRequestError ? err.code : 'apiToken.revoke.failed';
        if (code === 'apiToken.revoke.notFound') {
          // Already gone, which is what the user wanted.
          setTokens((previous) => previous.filter((entry) => entry.id !== id));
          return true;
        }
        console.warn('[profile] apiToken.revoke failed', code);
        setError(tRef.current('profile.apiTokenRevokeFailed'));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [request]
  );

  const clearError = useCallback(() => setError(null), []);

  // List once per sign-in. Keyed on whether a token exists, not its value: a refreshed access
  // token must not trigger another round trip.
  const hasToken = Boolean(token);
  useEffect(() => {
    if (!hasToken) {
      setTokens([]);
      return;
    }
    void refresh();
  }, [hasToken, refresh]);

  return { tokens, loading, busy, error, refresh, create, revoke, clearError };
};
